package com.qrkontaq.tenant

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.qrkontaq.core.viewmodel.BaseViewModel
import com.qrkontaq.data.dto.PublicMenuDto
import com.qrkontaq.data.dto.PublicMenuItemDto
import com.qrkontaq.data.dto.PublicRestaurantDto
import com.qrkontaq.data.repository.PublicRepository
import com.qrkontaq.ui.tenant.GuestApi
import com.qrkontaq.ui.tenant.GuestSession
import kotlinx.coroutines.launch

sealed interface MenuUiState {
    data object Loading : MenuUiState

    data object NotFound : MenuUiState

    /** Subscription expired: show the frozen page with restaurant branding if available. */
    data class Frozen(val restaurant: PublicRestaurantDto?) : MenuUiState

    data class Ready(
        val restaurant: PublicRestaurantDto,
        val menu: PublicMenuDto,
    ) : MenuUiState {
        val popular: List<PublicMenuItemDto> by lazy {
            menu.categories.flatMap { it.items }
                .filter { it.likes > 0 && it.isAvailable }
                .sortedByDescending { it.likes }
                .take(8)
        }

        val featured: List<PublicMenuItemDto> by lazy {
            menu.categories.flatMap { it.items }.filter { it.isFeatured && it.isAvailable }
        }
    }
}

class MenuViewModel(
    private val repository: PublicRepository,
    private val slug: String,
    private val tableLabel: String?,
    private val categorySlug: String?,
) : BaseViewModel() {

    var state: MenuUiState by mutableStateOf(MenuUiState.Loading)
        private set

    override fun onCreate() {
        super.onCreate()
        load()
        recordScan()
    }

    private fun load() {
        viewModelScope.launch {
            val restaurant =
                runCatching { repository.getRestaurant(slug) }.getOrNull()
            if (restaurant == null) {
                state = MenuUiState.NotFound
                return@launch
            }
            if (!restaurant.isLive) {
                state = MenuUiState.Frozen(restaurant)
                return@launch
            }
            val menu = runCatching { repository.getMenu(slug) }.getOrNull()
            state =
                if (menu == null) {
                    MenuUiState.Frozen(restaurant)
                } else {
                    MenuUiState.Ready(restaurant, menu)
                }
        }
    }

    private fun recordScan() {
        // Scans come only from QR entry points (/t/{label} or /m/{slug}), not plain visits.
        if (tableLabel == null && categorySlug == null) return
        viewModelScope.launch {
            // Restore the cross-venue OAuth session first, so a signed-in diner's scan
            // carries their identity and the waiter can see them at the table.
            if (GuestSession.token == null) {
                runCatching { GuestApi.trySilentSession() }
            }
            runCatching { GuestApi.recordScan(slug, tableLabel, categorySlug) }
        }
    }
}
